import React, { useState, useEffect } from 'react';
import {
  Button,
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  WalletOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  FilterOutlined,
  CloseOutlined,
  DollarOutlined,
  CreditCardOutlined,
  BankOutlined,
  HomeOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { NotiModal } from '../../../components/Noti/NotiModal';
import {
  SourceWallet,
  IncomeSource,
  incomeService,
  formatCurrency,
} from '../../../services/incomeService';
import styles from './WalletPage.module.css';

// Cấu hình locale tiếng Việt
dayjs.locale('vi');

export const WalletPage: React.FC = () => {
  const [wallets, setWallets] = useState<SourceWallet[]>([]);
  const [sources, setSources] = useState<IncomeSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState<number | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<SourceWallet | undefined>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    source_id: undefined as number | undefined,
    search: '',
  });
  const [showSourceFilter, setShowSourceFilter] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const loadWallets = async () => {
    try {
      setLoading(true);
      const walletsData = await incomeService.getWallets();
      setWallets(walletsData);
    } catch (error) {
      console.error('Error in loadWallets:', error);
      message.error('Không thể tải danh sách ví');
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async () => {
    try {
      const sourcesData = await incomeService.getSources();
      setSources(sourcesData);
    } catch (error) {
      message.error('Không thể tải nguồn thu nhập');
    }
  };

  useEffect(() => {
    loadWallets();
    loadSources();
  }, []);

  const handleAddWallet = () => {
    // TODO: Implement add wallet modal
    message.info('Tính năng thêm ví sẽ được phát triển sau');
  };

  const handleEditWallet = (wallet: SourceWallet) => {
    // TODO: Implement edit wallet modal
    message.info('Tính năng chỉnh sửa ví sẽ được phát triển sau');
  };

  const handleDeleteWallet = async (id: number) => {
    try {
      await incomeService.deleteWallet(id);
      message.success('Xóa ví thành công');
      loadWallets();
      setIsDetailModalOpen(false);
    } catch (error: any) {
      // Hiển thị thông báo lỗi chính xác từ API
      const errorMessage = error.response?.data?.error || 'Có lỗi xảy ra khi xóa ví';
      message.error(errorMessage);
    }
  };

  const handleShowDeleteModal = (walletId: number) => {
    setWalletToDelete(walletId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (walletToDelete) {
      await handleDeleteWallet(walletToDelete);
      setShowDeleteModal(false);
      setWalletToDelete(null);
    }
  };

  const getSourceById = (id: number) => {
    return sources.find(s => s.id === id);
  };

  const getWalletIcon = (sourceIcon: string) => {
    // Map source icons to wallet icons
    const iconMap: { [key: string]: React.ReactNode } = {
      '💰': <DollarOutlined />,
      '💳': <CreditCardOutlined />,
      '🏦': <BankOutlined />,
      '🏠': <HomeOutlined />,
      '🛒': <ShoppingOutlined />,
    };
    return iconMap[sourceIcon] || <WalletOutlined />;
  };

  const getWalletBalanceColor = (balance: number) => {
    if (balance > 0) {
      return '#10B981'; // Green for positive balance
    } else if (balance < 0) {
      return '#EF4444'; // Red for negative balance
    }
    return '#6B7280'; // Gray for zero balance
  };

  const getWalletStatusColor = (isActive: boolean) => {
    return isActive ? '#10B981' : '#6B7280';
  };

  const handleWalletClick = (wallet: SourceWallet) => {
    setSelectedWallet(wallet);
    setIsDetailModalOpen(true);
  };

  const handleListHeaderClick = () => {
    setShowSourceFilter(!showSourceFilter);
  };

  const handleSourceSelect = (sourceId: number | undefined) => {
    setFilters(prev => ({ 
      ...prev, 
      source_id: sourceId 
    }));
    setShowSourceFilter(false);
  };

  const getSelectedSourceName = () => {
    if (!filters.source_id) return 'Tất cả nguồn';
    const source = sources.find(s => s.id === filters.source_id);
    return source?.name || 'Tất cả nguồn';
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilterApply = (newFilters: any) => {
    setFilters(newFilters);
  };

  const getFilterCount = () => {
    let count = 0;
    if (filters.source_id) count++;
    if (filters.search) count++;
    return count;
  };

  const handleDetailModalEdit = () => {
    if (selectedWallet) {
      handleEditWallet(selectedWallet);
    }
  };

  const handleDetailModalDelete = () => {
    if (selectedWallet) {
      handleShowDeleteModal(selectedWallet.id);
      setIsDetailModalOpen(false);
    }
  };

  // Filter wallets based on current filters
  const filteredWallets = wallets.filter(wallet => {
    if (filters.source_id && wallet.source_id !== filters.source_id) {
      return false;
    }
    if (filters.search && !wallet.name.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    return true;
  });

  const renderWalletCard = (wallet: SourceWallet) => {
    const source = getSourceById(wallet.source_id);
    const balanceColor = getWalletBalanceColor(wallet.current_balance);
    const statusColor = getWalletStatusColor(wallet.is_active);
    
    return (
      <div 
        key={wallet.id} 
        className={styles.walletCard}
        onClick={() => handleWalletClick(wallet)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.iconContainer}>
          {getWalletIcon(wallet.source_icon)}
        </div>
        
        <div className={styles.walletDetails}>
          <p className={styles.walletTitle}>
            {wallet.name}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span className={styles.source}>
              {wallet.source_icon} {wallet.source_name}
            </span>
            {wallet.description && (
              <span className={styles.description}>
                {wallet.description}
              </span>
            )}
          </div>
        </div>
        
        <div className={styles.walletInfo}>
          <p className={styles.balance} style={{ color: balanceColor }}>
            {formatCurrency(wallet.current_balance)}
          </p>
          <Tag 
            color={wallet.is_active ? 'success' : 'default'}
            style={{ fontSize: '10px', padding: '2px 6px' }}
          >
            {wallet.is_active ? 'Hoạt động' : 'Không hoạt động'}
          </Tag>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <WalletOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có ví nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm ví đầu tiên của bạn
      </div>
    </div>
  );

  const renderWalletDetailModal = () => {
    if (!selectedWallet) return null;

    const source = getSourceById(selectedWallet.source_id);
    const balanceColor = getWalletBalanceColor(selectedWallet.current_balance);

    return (
      <div className={styles.detailModal}>
        <div className={styles.detailHeader}>
          <div className={styles.detailIcon}>
            {getWalletIcon(selectedWallet.source_icon)}
          </div>
          <div className={styles.detailTitle}>
            <h3>{selectedWallet.name}</h3>
            <span className={styles.detailSource}>
              {selectedWallet.source_icon} {selectedWallet.source_name}
            </span>
          </div>
        </div>

        <div className={styles.detailContent}>
          <div className={styles.detailSection}>
            <h4>Thông tin ví</h4>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Số dư hiện tại:</span>
              <span className={styles.detailValue} style={{ color: balanceColor }}>
                {formatCurrency(selectedWallet.current_balance)}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Số dư ban đầu:</span>
              <span className={styles.detailValue}>
                {formatCurrency(selectedWallet.initial_balance)}
              </span>
            </div>
            {selectedWallet.description && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Mô tả:</span>
                <span className={styles.detailValue}>
                  {selectedWallet.description}
                </span>
              </div>
            )}
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Trạng thái:</span>
              <Tag color={selectedWallet.is_active ? 'success' : 'default'}>
                {selectedWallet.is_active ? 'Hoạt động' : 'Không hoạt động'}
              </Tag>
            </div>
          </div>

          {source && (
            <div className={styles.detailSection}>
              <h4>Thông tin nguồn thu nhập</h4>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Tên nguồn:</span>
                <span className={styles.detailValue}>
                  {source.icon} {source.name}
                </span>
              </div>
              {source.description && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Mô tả:</span>
                  <span className={styles.detailValue}>
                    {source.description}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.detailActions}>
          <Button 
            onClick={handleDetailModalEdit}
            icon={<EditOutlined />}
            className={styles.actionButton}
          >
            Chỉnh sửa
          </Button>
          <Button 
            onClick={handleDetailModalDelete}
            danger
            icon={<DeleteOutlined />}
            className={styles.actionButton}
          >
            Xóa
          </Button>
          <Button 
            onClick={() => setIsDetailModalOpen(false)}
            icon={<CloseOutlined />}
            className={styles.actionButton}
          >
            Đóng
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <h2>Quản Lý Ví</h2>
          <div className={styles.headerIcons}>
            <button className={styles.addButton} onClick={handleAddWallet}>
              <PlusOutlined />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className={styles.content}>
        <div className={styles.listHeader}>
          <div className={styles.headerLeft} onClick={handleListHeaderClick}>
            <h3>{getSelectedSourceName()}</h3>
            <DownOutlined style={{ 
              transform: showSourceFilter ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }} />
          </div>
          <div className={styles.headerRight}>
            <button className={styles.filterButton} onClick={handleFilterModalOpen}>
              <FilterOutlined />
              {getFilterCount() > 0 && (
                <span className={styles.filterBadge}>{getFilterCount()}</span>
              )}
            </button>
          </div>
        </div>

        {/* Source Filter Dropdown */}
        {showSourceFilter && (
          <div className={styles.sourceFilterDropdown}>
            <div 
              className={styles.sourceFilterOption}
              onClick={() => handleSourceSelect(undefined)}
            >
              <span>Tất cả nguồn</span>
            </div>
            {sources.map((source) => (
              <div 
                key={source.id}
                className={styles.sourceFilterOption}
                onClick={() => handleSourceSelect(source.id)}
              >
                <span>{source.icon} {source.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Wallet Content */}
        <div className={styles.walletContent}>
          {filteredWallets.length > 0 ? (
            <>
              <div className={styles.walletGrid}>
                {filteredWallets.map(renderWalletCard)}
              </div>
              <div className={styles.summarySection}>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Tổng số ví:</span>
                    <span className={styles.summaryValue}>{filteredWallets.length}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Tổng số dư:</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(filteredWallets.reduce((sum, wallet) => sum + wallet.current_balance, 0))}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            renderEmptyState()
          )}
        </div>
      </main>

      {/* Detail Modal */}
      {isDetailModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsDetailModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            {renderWalletDetailModal()}
          </div>
        </div>
      )}

      {/* Modal xác nhận xóa */}
      <NotiModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa"
        message="Bạn có chắc chắn muốn xóa ví này?"
        confirmText="Xóa"
        cancelText="Hủy"
      />
    </div>
  );
};
